import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useDebouncedEffect, DateUtils } from '@grandvisionhq/utils';
import { storeFastAppointmentClickEvent, storeBookAppointmentClickEvent, storeChangeAppointmentTypeClickEvent } from '../../data-layer/actions.js';
import '@grandvisionhq/graphql';
import '../../queries/get-availability-stores-nearby.js';
import '@grandvisionhq/stores-v2';
import '../../queries/book-appointment.js';
import '../../queries/book-appointment-by-id.js';
import '../../queries/get-user.js';
import '../../queries/get-calendar-availability.js';
import '../../queries/get-calendar-availablility-by-id.js';
import '../../queries/get-store-offer-by-code.js';
import '../../queries/get-store-offer-by-id.js';
import '../../queries/get-store-opening-times.js';
import '../../queries/get-store-opening-times-by-id.js';
import '../../queries/get-time-slots.js';
import '../../queries/get-time-slots-by-id.js';
import 'date-fns';
import useCalendarAvailabilityById from '../../hooks/api/use-calendar-availability-by-id.js';
import { useStoreOpeningTimesById } from '../../hooks/api/use-store-opening-times-by-id.js';
import useTimeSlotsById from '../../hooks/api/use-time-slots-by-id.js';
import '../../pages/BookAppointment/queries/get-book-appointment-store.js';
import useBookAppointmentStoreById from '../../pages/BookAppointment/hooks/api/use-book-appointment-store-by-id.js';
import useBookAppointmentType from '../../pages/BookAppointment/hooks/api/use-book-appointment-type.js';
import { getClosedDates } from '../AvailabilityCalendar/utils.js';
import { useNextAvailableDate } from '../NoTimeSlots/hooks/use-next-available-date.js';
import { useNextWeekDate } from '../NoTimeSlots/hooks/use-next-week-date.js';
import { useNoTimeSlotsVariant } from '../NoTimeSlots/hooks/use-no-timeslot-variant.js';
import { Variant } from '../NoTimeSlots/types.js';
import { DropInWidget } from './DropInWidget.js';

const hasRequiredUrls = (urls)=>typeof urls.bookAppointment !== 'undefined'
;
const sanitizeUrls = (urls)=>{
    var ref;
    const sanitizedBookAppointmentUrl = (ref = urls.bookAppointment) === null || ref === void 0 ? void 0 : ref.replace(/\/+(where|what|when|who|confirmation)+/, '');
    return swcHelpers.objectSpread({}, urls, {
        bookAppointment: sanitizedBookAppointmentUrl
    });
};
const DropInWidgetContainer = ({ urls , storeId , typeId , dataLayerEvents ,  })=>{
    const { featureConfigs  } = useFeatureToggles();
    const { getFormattedLabel , getLabel  } = useIntl();
    const [storeQuery, storeQueryStatus] = useBookAppointmentStoreById() // TODO: MACC-19077 Rename useBookAppointmentStoreById merging to master
    ;
    const [typeQuery, typeQueryStatus] = useBookAppointmentType();
    const [getCalendarAvailability, calendarAvailability] = useCalendarAvailabilityById();
    const [getStoreOpeningTimes, storeOpeningTimes] = useStoreOpeningTimesById();
    const [getTimeSlots, timeSlots] = useTimeSlotsById();
    const [weekDate, setWeekDate] = React.useState();
    const [weeks1, setWeeks] = React.useState();
    const [date1, setDate] = React.useState();
    const [availabilityRequestParamsChanged, setAvailabilityRequestParamsChanged] = React.useState(true);
    const [timeSlot1, setTimeSlot] = React.useState();
    const appointmentBookingAvailabiltyCalendarWeekCount = featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.appointmentBookingAvailabiltyCalendarWeekCount;
    // We need the urls without a specific route
    const sanitizedUrls = React.useMemo(()=>sanitizeUrls(urls)
    , [
        urls
    ]);
    const calendarLoading = storeQueryStatus.loading || typeQueryStatus.loading || storeOpeningTimes.loading || calendarAvailability.loading || availabilityRequestParamsChanged;
    const loading = storeQueryStatus.loading || typeQueryStatus.loading;
    const store = storeQueryStatus.data;
    const type = typeQueryStatus.data;
    const labels = React.useMemo(()=>({
            title: getLabel('appointments.component.dropInWidget.title'),
            date: getLabel('appointments.component.dropInWidget.date'),
            type: getLabel('appointments.component.dropInWidget.type'),
            selectATime: getLabel('appointments.component.dropInWidget.selectATime'),
            bookAppointment: getLabel('appointments.component.dropInWidget.bookAppointment'),
            alert: getFormattedLabel('appointments.component.appointmentTypes.noAppointmentsAlert', {
                parseLink: true
            })
        })
    , [
        getFormattedLabel,
        getLabel
    ]);
    const preferredTimeLabels = React.useMemo(()=>({
            title: getLabel('appointments.component.dropInWidget.preferredTime.title')
        })
    , [
        getLabel
    ]);
    const closedDates = React.useMemo(()=>{
        if (!weeks1 || !storeOpeningTimes.data) {
            return [];
        }
        return getClosedDates(weeks1, storeOpeningTimes.data);
    }, [
        weeks1,
        storeOpeningTimes.data
    ]);
    useDebouncedEffect(()=>{
        var ref;
        const appointmentTypeId = type === null || type === void 0 ? void 0 : type.id;
        const globalStoreId = store === null || store === void 0 ? void 0 : store.globalStoreId;
        if (!appointmentTypeId || !globalStoreId || !weeks1) {
            /* istanbul ignore else */ if (process.env.NODE_ENV === 'test') {
                console.log('Awaiting availability request variables data');
            }
            return;
        }
        const firstWeekDay = (ref = weeks1[0]) === null || ref === void 0 ? void 0 : ref[0];
        const lastWeek = weeks1[weeks1.length - 1];
        const lastWeekDay = lastWeek === null || lastWeek === void 0 ? void 0 : lastWeek[lastWeek.length - 1];
        if (firstWeekDay && lastWeekDay) {
            getCalendarAvailability({
                appointmentTypeId,
                globalStoreId,
                startDate: firstWeekDay,
                endDate: lastWeekDay
            });
        }
        setAvailabilityRequestParamsChanged(false);
    }, 400, [
        getCalendarAvailability,
        type,
        store,
        weeks1
    ]);
    const onCalendarWeeksChange = React.useCallback((weeks)=>{
        setAvailabilityRequestParamsChanged(true);
        setWeeks(weeks);
    }, []);
    const onSelectDate = React.useCallback((date)=>{
        setDate(date);
    }, []);
    const nextAvailableDate = useNextAvailableDate(date1, calendarAvailability, Boolean(!timeSlots.called || calendarLoading));
    const nextWeekDate = useNextWeekDate(weeks1, !date1 || Boolean(nextAvailableDate));
    const noTimeSlotsVariant = useNoTimeSlotsVariant(timeSlots, nextAvailableDate, date1, closedDates, calendarLoading);
    const onSelectTimeSlot = React.useCallback((timeSlot)=>setTimeSlot(timeSlot)
    , []);
    const onBookAppointment = React.useCallback(()=>{
        // This should never happen, but is here to satisfy ts.
        if (!store) {
            return;
        }
        if (dataLayerEvents) {
            const dataLayerEvent = timeSlot1 && type ? storeFastAppointmentClickEvent({
                event: dataLayerEvents.storeAppointmentClickEvent,
                storeDetails: swcHelpers.objectSpread({
                    id: store.globalStoreId,
                    city: store.town
                }, store),
                selectedDateTime: timeSlot1.from,
                appointmentType: type.id.toString()
            }) : storeBookAppointmentClickEvent({
                event: dataLayerEvents.storeAppointmentClickEvent,
                storeDetails: swcHelpers.objectSpread({
                    id: store.globalStoreId,
                    city: store.town
                }, store)
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
        // No further selections were performed, skip to what step
        if (!type || !timeSlot1) {
            window.location.assign(`${sanitizedUrls.bookAppointment}/what?storeId=${store.globalStoreId}`);
            return;
        }
        // Skip to aab/who step
        window.location.assign(`${sanitizedUrls.bookAppointment}/who?storeId=${store.globalStoreId}&typeId=${type.id}&start=${timeSlot1.from.getTime()}&end=${timeSlot1.until.getTime()}`);
    }, [
        dataLayerEvents,
        sanitizedUrls,
        store,
        type,
        timeSlot1
    ]);
    const onSelectedTypeContainerClick = React.useCallback(()=>{
        // This should never happen, but is here to satisfy ts.
        if (!store) {
            return;
        }
        if (dataLayerEvents) {
            const dataLayerEvent = storeChangeAppointmentTypeClickEvent({
                event: dataLayerEvents.storeChangeAppointmentTypeClickEvent,
                storeDetails: swcHelpers.objectSpread({
                    id: store.globalStoreId,
                    city: store.town
                }, store)
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        store,
        dataLayerEvents
    ]);
    React.useEffect(()=>{
        if (!storeId) {
            return;
        }
        storeQuery(storeId);
    }, [
        storeId,
        storeQuery
    ]);
    React.useEffect(()=>{
        if (!typeId) {
            return;
        }
        typeQuery(typeId);
    }, [
        typeId,
        typeQuery
    ]);
    React.useEffect(()=>{
        if (!store) {
            return;
        }
        if (!store) {
            return;
        }
        getStoreOpeningTimes({
            variables: {
                globalStoreId: store.globalStoreId
            }
        });
    }, [
        getStoreOpeningTimes,
        store
    ]);
    const noTimeSlotsAction = React.useCallback(()=>{
        switch(noTimeSlotsVariant){
            case Variant.date:
            case Variant.closedDate:
                /* istanbul ignore if */ if (!nextAvailableDate) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                setDate(nextAvailableDate);
                break;
            case Variant.week:
            case Variant.closedWeek:
                /* istanbul ignore if */ if (!nextWeekDate) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                setWeekDate(nextWeekDate);
                setDate(nextWeekDate);
                break;
            case Variant.error:
                const globalStoreId = store === null || store === void 0 ? void 0 : store.globalStoreId;
                var ref;
                const appointmentTypeId = (ref = type === null || type === void 0 ? void 0 : type.id) !== null && ref !== void 0 ? ref : undefined;
                /* istanbul ignore if */ if (!date1 || globalStoreId === undefined || appointmentTypeId === undefined) {
                    // This should never happen, but it's here to satisfy typescript
                    return;
                }
                getTimeSlots({
                    variables: {
                        globalStoreId,
                        appointmentTypeId,
                        date: DateUtils.toLocalDate(date1)
                    }
                });
            // Time filter doesn't exist on SDP, as opposed to AAB.
            case Variant.time:
            case Variant.none:
                return;
        }
    }, [
        noTimeSlotsVariant,
        nextAvailableDate,
        nextWeekDate,
        getTimeSlots,
        store,
        type,
        date1
    ]);
    React.useEffect(()=>{
        calendarAvailability.error && console.error('Error fetching appointment availability:', calendarAvailability.error.message);
    }, [
        calendarAvailability.error
    ]);
    React.useEffect(()=>{
        const globalStoreId = store === null || store === void 0 ? void 0 : store.globalStoreId;
        var ref;
        const appointmentTypeId = (ref = type === null || type === void 0 ? void 0 : type.id) !== null && ref !== void 0 ? ref : undefined;
        /* istanbul ignore if */ if (!date1 || globalStoreId === undefined || appointmentTypeId === undefined) {
            // This should never happen, but it's here to satisfy typescript
            return;
        }
        getTimeSlots({
            variables: {
                globalStoreId,
                appointmentTypeId,
                date: DateUtils.toLocalDate(date1)
            }
        });
    }, [
        getTimeSlots,
        store,
        type,
        date1
    ]);
    if (!hasRequiredUrls(sanitizedUrls)) {
        console.error('Cannot render DropInWidget, missing required urls.');
        return null;
    }
    return /*#__PURE__*/ React.createElement(DropInWidget, {
        urls: sanitizedUrls,
        loading: loading,
        labels: labels,
        store: store,
        type: type,
        onCalendarWeeksChange: onCalendarWeeksChange,
        selectedDate: date1,
        onSelectDate: onSelectDate,
        calendarLoading: calendarLoading,
        calendarWeekDate: weekDate,
        calendarWeekCount: appointmentBookingAvailabiltyCalendarWeekCount,
        calendarClosedDates: closedDates,
        calendarAvailability: calendarAvailability.data,
        timeSlotsLoading: timeSlots.loading,
        timeSlots: timeSlots.filtered.included,
        onSelectTime: onSelectTimeSlot,
        selectedTimeSlot: timeSlot1,
        noTimeSlotsVariant: noTimeSlotsVariant,
        noTimeSlotsAction: noTimeSlotsAction,
        nextAvailableDate: nextAvailableDate,
        noMoreAvailabilityUntilDate: nextWeekDate,
        onBookAppointment: onBookAppointment,
        preferredTimeLabels: preferredTimeLabels,
        onSelectedTypeContainerClick: onSelectedTypeContainerClick
    });
};

export { DropInWidgetContainer, DropInWidgetContainer as default };
//# sourceMappingURL=DropInWidgetContainer.js.map
